import { encode as base64_encode } from "base-64";
import { fromZonedTime, toZonedTime } from "date-fns-tz";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  useCreateCustomReportMutation,
  useLazyGetReportsByCatTypeQuery,
  useLazyGetReportSpecificFieldsQuery,
  useModifyCustomReportMutation,
} from "../../../api/network/reportApiServices";
import {
  setModalsData,
  setReportsByType,
  setReportVisibleColumns,
  TModalsState,
} from "../../../api/store/commonSlice";
import {
  ModifyCustomReportPayloadField,
  TReport,
  TReportFilter,
} from "../../../api/types/Report";
import { TCustomizableItemList } from "../../../components/types";
import { APP_CONFIG, REPORT_TYPES } from "../../../constants/constants";
import { useAccountTimezone } from "../../../hooks/timezone";
import { routeUrls } from "../../../navigation/routeUrls";
import {
  dbTimeToJsTime,
  formattedDateTimeBasedOnTimezone,
  jsTimeToDBTime,
} from "../../../utils/dateTime";
import {
  localStorageKeys,
  useLocalStorage,
} from "../../../utils/localStorageItems";
import {
  TReportOptionsSubmitCallback,
  TReportOptionsSubmitCallbackProps,
} from "../components/reportOptions";
import {
  REPORT_FILTER_TYPES,
  STANDARD_REPORT_TIME_RANGE_TIMESTAMP,
} from "../constants";
import { filterReportFiltersCallback } from "../reportUtil";
import { ListingResponse } from "../../../api/types/Listing";

export type TCreateCustomReportCallbackProps =
  TReportOptionsSubmitCallbackProps & {
    reportName: string;
    selectedFields: ModifyCustomReportPayloadField[];
    reportFilters: TReportFilter[];
  };

export type TModifyCustomReportCallbackProps =
  TReportOptionsSubmitCallbackProps & {
    modifyReportId: number;
    reportName?: string;
    selectedFields?: ModifyCustomReportPayloadField[];
    reportFilters?: TReportFilter[];
    filtersUpdateRequired?: boolean; // used to idenfify if filters need to be updated
  };

export type TCreateModifyCustomReportCallbackProps =
  | TCreateCustomReportCallbackProps
  | TModifyCustomReportCallbackProps;

export type TCreateModifyCustomReportCallback = (
  props: TCreateModifyCustomReportCallbackProps
) => void;

export const useReportUtils = () => {
  const { t: tMain } = useTranslation();
  const { t } = useTranslation("translation", {
    keyPrefix: "reports.addNewReport",
  });
  const { t: tSelectAReportType } = useTranslation("translation", {
    keyPrefix: "reports.selectAType",
  });
  const navigate = useNavigate();
  const { setLocalStorageItem } = useLocalStorage();

  const { convertToTZForDateFns, tzForDateFns, tzForDisplay } =
    useAccountTimezone();

  const modalsState: TModalsState = useSelector(
    (state: any) => state.commonReducer.modals
  );
  const dispatch = useDispatch();

  const [
    createCustomReportAPITrigger,
    { isLoading: isLoadingCreateCustomReport },
  ] = useCreateCustomReportMutation();
  const [
    modifyCustomReportAPITrigger,
    { isLoading: isLoadingModifyCustomReport },
  ] = useModifyCustomReportMutation();
  const [
    getReportsByCatTypeAPITrigger,
    { isFetching: isFetchingReportsByCatType },
  ] = useLazyGetReportsByCatTypeQuery();

  const [
    getSpecificReportQuery /*, { isFetching: isFetchingSpecificReportQuery }*/,
  ] = useLazyGetReportSpecificFieldsQuery();

  // get unix timestamp from date and time in the selected timezone
  // deprecated Dec 20, 2024 - was added by peer and not in use
  const getUnixTimestampForDateTime = (
    date: string | Date,
    timezone: string
  ) => {
    let calculatedUnixTimestamp;
    const selectedDate = new Date(date); // `${filterData.date} ${filterData.time}:00`
    const utcDate = selectedDate;
    // fromZonedTime(selectedDate, timezone);
    const jsTime = new Date(utcDate).getTime();
    if (!!jsTime && !isNaN(jsTime)) {
      calculatedUnixTimestamp = formattedDateTimeBasedOnTimezone(
        date,
        convertToTZForDateFns(timezone),
        "t"
      );
      if (APP_CONFIG.DEBUG.ALL)
        console.log("unixTimestamp", calculatedUnixTimestamp);
    } else {
      if (APP_CONFIG.DEBUG.ALL)
        console.error("[getUnixTimestampForDateTime] Invalid datetime", jsTime);
    }
    return calculatedUnixTimestamp;
  };

  // calculate unix timestamps for start_time and end_time, and return back all values
  // deprecated Dec 20, 2024 as we are not using separate filter values for start_time and end_time
  // const getValuesWithCalcUnixTimestamps = (valueParams: any) => {
  //   if (APP_CONFIG.DEBUG.ALL)
  //     console.log("[getValuesWithCalcUnixTimestamps] INPUT", valueParams);
  //   let calculatedUnixTimestampFrom = null;
  //   let calculatedUnixTimestampTo = null;
  //   const applicableTimezone =
  //     valueParams?.timezone || tzForDisplay || APP_CONFIG.DEFAULT_TIMEZONE;

  //   const defaultDate = new Date();
  //   defaultDate.setDate(defaultDate.getDate() - 1);
  //   // defaultDate.setHours(0, 0, 0, 0);

  //   let usableValues = {
  //     ...valueParams,
  //     ...(!!valueParams?.start_time
  //       ? !isNaN(valueParams?.start_time) // is a number
  //         ? {
  //             start_time:
  //               // fixed the issue of start_time drifting by some duration on every update
  //               formattedDateTimeBasedOnTimezone(
  //                 dbTimeToJsTime(parseInt(`${valueParams?.start_time}`)),
  //                 convertToTZForDateFns(applicableTimezone),
  //                 "yyyy-MM-dd'T'HH:mm"
  //               ),
  //           }
  //         : {
  //             // is a string
  //             start_time: valueParams?.start_time,
  //           }
  //       : {
  //           // set default start_time if not present
  //           start_time: `${defaultDate.toISOString().split("T")[0]}T00:00`,
  //         }),
  //     ...(!!valueParams?.end_time
  //       ? !isNaN(valueParams?.end_time)
  //         ? {
  //             end_time:
  //               // fixed the issue of end_time drifting by some duration on every update
  //               formattedDateTimeBasedOnTimezone(
  //                 dbTimeToJsTime(parseInt(`${valueParams?.end_time}`)),
  //                 convertToTZForDateFns(applicableTimezone),
  //                 "yyyy-MM-dd'T'HH:mm"
  //               ),
  //           }
  //         : {
  //             // is a string
  //             end_time: valueParams?.end_time,
  //           }
  //       : {
  //           // set default end_time if not present
  //           end_time: `${defaultDate.toISOString().split("T")[0]}T23:59`,
  //         }),
  //     timezone: applicableTimezone,
  //   }; // || values;
  //   if (
  //     !!valueParams?.start_time &&
  //     typeof valueParams?.start_time === "number"
  //   ) {
  //     // this fixes the issue of start_time drifting by some duration on every update
  //     calculatedUnixTimestampFrom = valueParams?.start_time;
  //   } else {
  //     // following seems redundant, but is kept for time being
  //     if (!!usableValues.timezone && !!usableValues.start_time) {
  //       if (APP_CONFIG.DEBUG.ALL)
  //         console.log("====== START", usableValues.start_time);
  //       const selectedDate = usableValues.start_time; // :00
  //       try {
  //         calculatedUnixTimestampFrom =
  //           fromZonedTime(
  //             Number(
  //               formattedDateTimeBasedOnTimezone(
  //                 usableValues.start_time,
  //                 convertToTZForDateFns(usableValues.timezone),
  //                 "t"
  //               )
  //             ) * 1000,
  //             convertToTZForDateFns(usableValues.timezone)
  //           ).getTime() / 1000;
  //         // if (APP_CONFIG.DEBUG.ALL) {
  //         //   console.log(
  //         //     "====",
  //         //     selectedDate,
  //         //     usableValues.timezone,
  //         //     calculatedUnixTimestampFrom
  //         //   );
  //         //   console.log(
  //         //     toZonedTime(
  //         //       Number(calculatedUnixTimestampFrom) * 1000,
  //         //       convertToTZForDateFns(usableValues.timezone)
  //         //     )
  //         //   );
  //         // }
  //       } catch (e) {
  //         if (APP_CONFIG.DEBUG.ALL) console.error("error", e);
  //       }
  //     }
  //   }
  //   if (!!valueParams?.end_time && typeof valueParams?.end_time === "number") {
  //     // this fixes the issue of end_time drifting by some duration on every update
  //     calculatedUnixTimestampTo = valueParams?.end_time;
  //   } else {
  //     // following seems redundant, but is kept for time being
  //     if (!!usableValues.timezone && !!usableValues.end_time) {
  //       if (APP_CONFIG.DEBUG.ALL)
  //         console.log("====== END", usableValues.end_time);
  //       const selectedDate = usableValues.end_time; // :00
  //       try {
  //         calculatedUnixTimestampTo =
  //           fromZonedTime(
  //             Number(
  //               formattedDateTimeBasedOnTimezone(
  //                 usableValues.end_time,
  //                 convertToTZForDateFns(usableValues.timezone),
  //                 "t"
  //               )
  //             ) * 1000,
  //             convertToTZForDateFns(usableValues.timezone)
  //           ).getTime() / 1000;
  //         // if (APP_CONFIG.DEBUG.ALL) {
  //         //   console.log(
  //         //     "====",
  //         //     selectedDate,
  //         //     usableValues.timezone,
  //         //     calculatedUnixTimestampTo
  //         //   );
  //         //   console.log(
  //         //     toZonedTime(
  //         //       Number(calculatedUnixTimestampTo) * 1000,
  //         //       convertToTZForDateFns(usableValues.timezone)
  //         //     )
  //         //   );
  //         // }
  //       } catch (e) {
  //         if (APP_CONFIG.DEBUG.ALL) console.error("error", e);
  //       }
  //     }
  //   }
  //   // if (Object.keys(values).includes('start_time')) setFieldValue("calculatedUnixTimestampFrom", calculatedUnixTimestampFrom);
  //   // if (Object.keys(values).includes('end_time')) setFieldValue("calculatedUnixTimestampTo", calculatedUnixTimestampTo);
  //   const outputValues = {
  //     ...usableValues,
  //     ...(Object.keys(valueParams).includes("start_time") &&
  //     !!calculatedUnixTimestampFrom
  //       ? { calculatedUnixTimestampFrom }
  //       : {}),
  //     ...(Object.keys(valueParams).includes("end_time") &&
  //     !!calculatedUnixTimestampTo
  //       ? { calculatedUnixTimestampTo }
  //       : {}),
  //   };
  //   if (APP_CONFIG.DEBUG.ALL)
  //     console.log("[getValuesWithCalcUnixTimestamps] OUTPUT", outputValues);
  //   return outputValues;
  // };

  const navigateOnReportOptionsChange: TReportOptionsSubmitCallback = ({
    filterValues,
    reportType,
    category,
    reportId,
    navigate,
    reportFilters,
  }) => {
    // const { time_toggle, ...restReportFilters } = filterValues;
    if (!!filterValues?.time_toggle) {
      delete filterValues.time_toggle;
    }

    // calculate unix timestamps
    const restReportFiltersWithUnixTimestamps = !!filterValues
      ? filterValues
      : {};

    // prepare report filters - json encode and base64 encode
    const reportFiltersB64 = base64_encode(
      JSON.stringify({
        ...restReportFiltersWithUnixTimestamps,
        nonce: Date.now(), // to trigger re-fetch of report data on any and every change
      })
    ).replace(/=/g, "");

    // navigate to generate report
    const newUrl =
      routeUrls.dashboardChildren.reportsChildren.reportPrefix +
      routeUrls.dashboardChildren.reportsChildren.reportSuffixParamsUri
        .replace(":reportType", reportType)
        .replace(":categoryId", `${category}`)
        .replace(":reportId", `${reportId}`)
        .replace(
          ":reportFilters",
          !!filterValues && Object.keys(filterValues).length > 0
            ? reportFiltersB64
            : ""
        );
    if (APP_CONFIG.DEBUG.ALL) console.log("newUrl", newUrl);
    navigate(newUrl);
  };

  const createModifyCustomReportWithFilters: TCreateModifyCustomReportCallback =
    (props) => {
      const {
        reportName,
        filterValues,
        reportType,
        category,
        reportId,
        selectedFields,
        reportFilters,
        finallyCallback,
      } = props;
      const { modifyReportId } = props as TModifyCustomReportCallbackProps;
      if (APP_CONFIG.DEBUG.REPORTS)
        console.log("[CREATE/MOD] filterValues", filterValues);
      // console.log('reportType', reportType);
      // console.log('category', category);
      // console.log('reportId', reportId);
      // console.log('selectedFields', selectedFields);
      if (APP_CONFIG.DEBUG.REPORTS)
        console.log("[CREATE/MOD] reportFilters", reportFilters);

      const paramsReportFields = {
        report_fields: (selectedFields || []).map((field) => {
          return {
            ...field,
            aggregate_function: field.aggregate_function,
            column_type: field.column_type,
            value: field.value,
            operator: field.operator,
          };
        }),
      };
      const paramsReportFilters = {
        report_filters: [
          // basic filters
          ...Object.keys(filterValues || [])
            // filter out non basic filters
            .filter(
              (key) =>
                !reportFilters?.find(
                  (def) =>
                    def.param === key &&
                    def.filter_type !== REPORT_FILTER_TYPES.BASIC
                )
            )
            // ensure that only specific fields are sent to BE for creating/updating custom reports
            .filter((key) =>
              filterReportFiltersCallback(
                key,
                reportFilters || ([] as TReportFilter[])
              )
            )
            // sanitize objects to include the required keys like `param`, `value`, `filter_type`, `operator`
            .map((key) => {
              const field_config = Object.keys(
                APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS
              ).find(
                (config_key) =>
                  APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS[
                    config_key as keyof typeof APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS
                  ]?.field_name === key
              );
              const isMulti =
                APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS[
                  field_config as keyof typeof APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS
                ]?.multiple;
              return {
                param: key,
                value: Array.isArray(filterValues[key])
                  ? // && filterValuesExceptCalcTimestamps[key].length > 0
                    // && !!filterValuesExceptCalcTimestamps[key][0].value
                    filterValues[key].map((item: any) => item.value).join(",")
                  : filterValues[key],
                filter_type: reportFilters?.find((def) => def.param === key)
                  ?.filter_type,
                operator: reportFilters?.find((def) => def.param === key)
                  ?.operator,
              };
            }),
          // column filters / advanced filters
          ...(reportFilters || []).filter(
            (filter) => filter.filter_type !== REPORT_FILTER_TYPES.BASIC
          ),
        ],
      };
      // if paramsReportFilters has `time_range` filter, then make sure that `start_time` and `end_time` filters have a valid value (it can be current unix timestamp)
      // this is required by the backend
      const timeRangeFilter = paramsReportFilters.report_filters.find(
        (filter) => filter.param === "time_range"
      );
      if (!!timeRangeFilter && !!timeRangeFilter.value) {
        const currentTime = jsTimeToDBTime(new Date().getTime());
        paramsReportFilters.report_filters.map((filter) => {
          if (filter.param === "start_time") {
            filter.value = STANDARD_REPORT_TIME_RANGE_TIMESTAMP; // currentTime;
          }
          if (filter.param === "end_time") {
            filter.value = STANDARD_REPORT_TIME_RANGE_TIMESTAMP; //currentTime;
          }
          return filter;
        });
      }
      if (APP_CONFIG.DEBUG.REPORTS)
        console.log("[CREATE/MOD] paramsReportFilters", paramsReportFilters);

      const createParams = {
        linked_standard_report: reportId,
        name: reportName || "",
        ...paramsReportFields,
        ...paramsReportFilters,
      };
      // console.log('createParams', createParams);

      const modifyParams = {
        id: parseInt(modifyReportId?.toString() || "0"), // reportId.toString()
        ...(!!reportName ? { name: reportName } : {}),
        ...(!!selectedFields && selectedFields.length > 0
          ? paramsReportFields
          : {}),
        ...// basic filters check
        ((!!filterValues && Object.keys(filterValues).length > 0) ||
        // column / advanced filters check
        (Array.isArray(reportFilters) &&
          reportFilters?.filter(
            (filter) => filter.filter_type !== REPORT_FILTER_TYPES.BASIC
          ).length > 0)
          ? paramsReportFilters
          : (props as TModifyCustomReportCallbackProps).filtersUpdateRequired
            ? { report_filters: [] } // sending blank array ensures that all filters are removed
            : {}),
      };
      if (APP_CONFIG.DEBUG.REPORTS)
        console.log("[CREATE/MOD] modifyParams", modifyParams);

      (!!modifyReportId && reportType !== REPORT_TYPES.STANDARD
        ? modifyCustomReportAPITrigger(modifyParams)
        : createCustomReportAPITrigger(createParams)
      )
        .unwrap()
        .then((response) => {
          if (!response.id) {
            toast.error(t("toast.create_failed"));
            return;
          } else {
            // call getReportsByCatType API to get the latest reports
            // TODO: we could use refetchCustomReportListToStore() here
            getReportsByCatTypeAPITrigger({
              type: REPORT_TYPES.CUSTOM,
              page: 1,
              page_size: APP_CONFIG.LISTINGS.LARGE_PAGE_SIZE,
            })
              .unwrap()
              .then((responseReportsByCatType) => {
                // dispatch the reports to store
                dispatch(
                  setReportsByType({
                    type: REPORT_TYPES.CUSTOM,
                    reports: responseReportsByCatType.results,
                  })
                );

                console.log("====== CHK", selectedFields);

                // update columns in redux store and local storage
                const columnsByVisibility = (selectedFields || []).map(
                  (field) => String(field.field_title)
                );
                // dispatch(
                //   setReportVisibleColumns({
                //     type: localStorageKeys.columns.Reports(
                //       reportType,
                //       category.toString(),
                //       response.id.toString()
                //     ),
                //     columns: columnsByVisibility,
                //   })
                // );
                // setLocalStorageItem(
                //   localStorageKeys.columns.Reports(
                //     reportType,
                //     category.toString(),
                //     response.id.toString()
                //   ),
                //   columnsByVisibility
                // );

                // dispatch(setModalsData({ ...modalsState, showReportTypeSelector: false }));
                dispatch(
                  setModalsData({
                    ...modalsState,
                    showCustomReportOptions: false,
                    showUpdateReportOptions: false,
                    showAddEditReportModal: false,
                  })
                );
                // setSelectedFields([]); // TODO: disabled for now, need to check if this is required

                // navigate to generate report
                navigateOnReportOptionsChange({
                  filterValues: response.report_filters
                    ?.filter(
                      // only basic filters should be sent in the url as params
                      // (this is needed because, multiple filters can be applied on the same field at different levels)
                      (item) => item.filter_type === REPORT_FILTER_TYPES.BASIC
                    )
                    ?.reduce((acc, filter) => {
                      const field_config_key = Object.keys(
                        APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS
                      ).find(
                        (config_key) =>
                          APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS[
                            config_key as keyof typeof APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS
                          ]?.field_name === filter.param
                      );
                      const field_config =
                        APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS[
                          field_config_key as keyof typeof APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS
                        ];
                      if (!!field_config && !!field_config?.multiple) {
                        acc[filter.param] =
                          filter.value === ""
                            ? []
                            : filter.value.split(",").map((item: any) => ({
                                label: item,
                                value: item,
                              }));
                      } else {
                        acc[filter.param] = filter.value;
                      }
                      return acc;
                    }, {} as any),
                  reportType,
                  category,
                  reportId: response.id,
                  navigate,
                  reportFilters: response.report_filters,
                });

                if (!!finallyCallback) finallyCallback(true);
              })
              .catch((error) => {
                if (APP_CONFIG.DEBUG.REPORTS)
                  console.log(
                    "createModifyCustomReportWithFilters error",
                    error
                  );
                // it is highly unlikely that this API will fail, however, if it does, we should show a message
                // toast.error(tMain("toast.general_error"));
                // console.error('getReportsByCatTypeAPITrigger error', error);
                if (!!finallyCallback) finallyCallback(false);
              })
              .finally(() => {
                // instead of here, we are calling it in the `then` and `catch` blocks
                // if (!!finallyCallback) finallyCallback();
              });
          }
        })
        .catch((error) => {
          dispatch(
            setModalsData({ ...modalsState, showCustomReportOptions: false })
          );

          // error message display is being handled in the `reportApiService.ts` API service
          // toast.error(tMain("toast.general_error"));

          if (!!finallyCallback) finallyCallback(false);
        });
      // instead of calling finally, we are calling it in the `then` and `catch` blocks
      // .finally(() => {
      //   if (!!finallyCallback) finallyCallback();
      // });
    };

  // refetch custom report list to store
  const refetchCustomReportListToStore = (successCallback?: () => void) => {
    getReportsByCatTypeAPITrigger({
      type: REPORT_TYPES.CUSTOM,
      page: 1,
      page_size: APP_CONFIG.LISTINGS.LARGE_PAGE_SIZE,
    })
      .unwrap()
      .then((responseReportsByCatType: ListingResponse<TReport[]>) => {
        // dispatch the reports to store
        dispatch(
          setReportsByType({
            type: REPORT_TYPES.CUSTOM,
            reports: responseReportsByCatType.results,
          })
        );

        if (!!successCallback) successCallback();
      })
      .catch((error) => {
        if (APP_CONFIG.DEBUG.REPORTS)
          console.log("refetchCustomReportListToStore error", error);
        // it is highly unlikely that this will fail, but just in case
        // toast.error(tMain("toast.general_error"));
      });
  };

  const navigateToReportPage = (
    reportId: number,
    reportTypeId: REPORT_TYPES
  ) => {
    getSpecificReportQuery({
      report_id: reportId,
      type: reportTypeId,
    })
      .unwrap()
      .then((response) => {
        const reportFilters = response?.report_filters?.reduce(
          (acc, filter) => {
            acc[filter.param] = filter.value;
            return acc;
          },
          {} as { [key: string]: string }
        );

        // navigate to generate report
        // handleReportOptionsSubmit(reportFilters, REPORT_TYPES.CUSTOM, subReport.category, subReport.id, navigate);
        navigateOnReportOptionsChange({
          filterValues: reportFilters,
          reportType: reportTypeId,
          category: response.category,
          reportId: reportId,
          navigate,
          reportFilters: response?.report_filters,
        });
        dispatch(
          setModalsData({
            ...modalsState,
            showReportTypeSelector: false,
          })
        );
      })
      .catch((error) => {
        console.log("error", error);
        toast.error(tSelectAReportType("toast.get_specific_report_error"));
      });
  };

  return {
    createModifyCustomReportWithFilters,
    getUnixTimestampForDateTime,
    // getValuesWithCalcUnixTimestamps,
    navigateOnReportOptionsChange,
    refetchCustomReportListToStore,
    navigateToReportPage,
  };
};
